<template>
	<div>
		<Head>
			<Meta name="robots" :content="'index, follow'" />
		</Head>
		
        <Wallpaper v-if="showWallpaper" />
	
		<main :class="[!showWallpaper && 'distance-top']">
			<div 
				v-if="loggedIn === true"
				class="preview-notice-container"
				:class="!showPreviewLayout ? 'deactivated': ''"
				@click="togglePreviewMarker()"
			>
				<span v-if="showPreviewLayout">Preview Inhalte Markiert</span>
				<span v-else>Preview Inhalte Liveansicht</span>
			</div>

			<ScrollProgressBar v-if="showHistoryBack" />

			<article class="@container/article">
				<div 
					v-if="showReadingTime && loadAds"
					class="reading-time"
					:class="sections[0]?.meta.background_color"
				>
					<div class="container spacing-default">
						<span>{{ globals.pagecontent?.readingTime }}:</span> <span>{{ Math.round(readingTime) + 1 }} {{ globals.pagecontent?.readingTimeUnit }}</span>
					</div>
				</div>

				<section
					v-for="section in sections"
					:key="sections"
					:class="[section.meta.reverse_sorting && 'reverse-sorting', section.meta.background_color]"
					:style="[
						section.media.images?.length > 0 && `background-image: url(${section.media.images[0].path}); background-repeat: no-repeat`,
						section.media.images?.length > 0 && section.meta.switch && 'background-size: cover',
						section.media.images?.length > 0 && !section.meta.switch && 'background-size: contain',
						section.media.images?.length > 0 && `background-position: ${section.meta.positioning}`
					]"
				>
					<div class="container spacing-default grid grid-cols-12">
						<div
							v-for="widget in section.widgets"
							:class="{
								'preview-content': widget.meta.cVisible === 'hidden' && showPreviewLayout,
								'content-center': widget.meta.cType === 'image-widget' || widget.meta.vertical_alignment === 'center',
								'content-start': widget.meta.vertical_alignment === 'top',
								'content-end': widget.meta.vertical_alignment === 'bottom',
								'col-span-full': widget.meta.column_width === '12',
								'col-span-full md:col-span-11': widget.meta.column_width === '11',
								'col-span-full md:col-span-10': widget.meta.column_width === '10',
								'col-span-full md:col-span-9': widget.meta.column_width === '9',
								'col-span-full md:col-span-8': widget.meta.column_width === '8',
								'col-span-full md:col-span-7': widget.meta.column_width === '7',
								'col-span-full md:col-span-6': widget.meta.column_width === '6',
								'col-span-full md:col-span-5': widget.meta.column_width === '5',
								'col-span-full md:col-span-4': widget.meta.column_width === '4',
								'col-span-full md:col-span-3': widget.meta.column_width === '3' && widget.meta.cType !== 'text-image-widget',
								'col-span-full sm:col-span-6 md:col-span-4 lg:col-span-3': widget.meta.column_width === '3' && widget.meta.cType === 'text-image-widget',
								[`offset-${widget.meta.offset}`]: widget.meta.offset
							}"
						>
							<component
								v-if="widget.meta.cVisible !== 'hidden' || loggedIn === true"
								:is="widgetDOMs[widget.meta.cType]"
								:pID="widget.meta.cID"
								:source="widget.meta.cType === 'image-widget' && widget.media.images"
								:classes="widget.meta.cType === 'image-widget' && widget.meta.horizontal_alignment === 'center' && 'mx-auto block max-w-full'"
							></component>
						</div>
					</div>
				
				</section>
				
				<section v-if="showRelatedNews && loadAds" class="related-articles light-sand">
					<div class="container spacing-default grid grid-cols-12">
						<div  class="col-span-full">
							<h2>{{ $t('interest') }}</h2>
						</div>
						<div class="content-center col-span-full">
							<RelatedNews />
						</div>
					</div>
				</section>
			</article>

			<GetInTouch v-if="loadAds && pageMeta?.enable_get_in_touch" />
		</main>
	
		<PageFooter v-if="loadAds" />
	</div>
</template>

<script setup lang="ts">
	import { useBackendApiGet } from "@/composables/useBackendApi"
	import { type AuthResponse } from '@/types/AuthTypes'
	import { useRequestStore } from '@/stores/requests'

	const runtimeConfig = useRuntimeConfig()
	const nuxtApp = useNuxtApp();

	const route = useRoute()
	const { locale } = useI18n()
	
	const widgetDOMs = ref<Record<string, any>>({})
	const showHistoryBack = ref<boolean>(false)
	const readingTime = ref<number>(0)
	const showRelatedNews = ref<boolean>(false)
	const showReadingTime = ref<boolean>(false)
	const loadAds = ref<boolean>(false)
	
	const localForage = useLocalForage()
	const requestStore = useRequestStore()
	
	const { pageContent, globals, pageID, pageIndex, lang, loggedIn, showPreviewLayout } = storeToRefs(requestStore)

	definePageMeta({
		pageTransition: {
			name: 'page',
			// mode: 'out-in'
			mode: 'in-out'
		}
	})
	
	const importWidget = async(widget: string) => {
		const widgetDom = await import(`~/components/components/${widget}.vue`)

		widgetDOMs.value[widget] = markRaw(widgetDom.default)
	}

	const showWallpaper = computed(() => {
        const pagedata = pageContent.value[pageID.value]?.pagecontent
        let showIt
        
        if (
            pagedata?.wallpaper?.length > 0 ||
            pagedata?.images?.length > 0 ||
            pagedata?.longDescription ||
            pagedata?.iframe
        ) {
            showIt = true
        } else {
            showIt = false
        }

        return showIt
    })

	const togglePreviewMarker = () => {
		requestStore.togglePreviewMarker()
	}

	const sections = computed(() => {
		const pageWidgets: any = pageContent.value[pageID.value]?.pagecontent.widgets
		let sectionObj: any
		let finalObj: any = []

		pageWidgets?.forEach((widget: any) => {
			switch (widget.meta.cType) {
				case 'section-marker--start':
					sectionObj = {
						meta: widget.meta,
						widgets: [],
						media: {
							images: widget.media.images
						}
					}
					break;
				case 'section-marker--end':
					finalObj.push(sectionObj)
					sectionObj = {}
					break;
				default:
					importWidget(widget.meta.cType);
					sectionObj.widgets?.push(widget);
					break;
			}
		});

		return finalObj
	})

	const pageMeta = computed(() => {
		const metaData: any = pageContent.value[pageID.value]?.pagemeta

		return metaData
	})

	const getDynamicContentData = async (id: any, lang: string, initial: boolean = false) => {
		if (!id) {
			throw createError({
				statusCode: 404,
				statusMessage: 'Seite nicht gefunden',
				fatal: true
  			})
		}

		try {
			const data = await useBackendApiGet<AuthResponse>(`v1/microsite/${id}/${locale.value}?status=all`)

			if (data) {
				requestStore.addPageContent(pageID.value, data)

				if (initial) {
					const langObj = {[lang]: data}
					const obj = {[id]: langObj}

					await localForage.setItem('pagedata', obj)
				} else {
					let currentDB: any = await localForage.getItem('pagedata')
					
					if (currentDB && currentDB[id]) {
						currentDB[id][lang] = data

						await localForage.setItem('pagedata', currentDB)
					} else if (currentDB && !currentDB[id]) {
						const newObj = {[lang]: data}
						currentDB[id] = newObj

						await localForage.setItem('pagedata', currentDB)
					}
				}

				loadAds.value = true // Set base value for additionals like footer and geat in touch component to true
			}
		} catch (error) {
			console.error("Fehler beim Abrufen der Daten:", error);
			loadAds.value = true // Set base value for additionals like footer and geat in touch component to true
		}
	};

	const hydratePage = async (id: number, lang: string) => {
		// get content
		await localForage.setItem('currentPageID', id)

		const indexedDB: any = await localForage.getItem('pagedata')

		if (indexedDB && indexedDB[id] && indexedDB[id][lang]) {
			requestStore.addPageContent(pageID.value, indexedDB[pageID.value][lang])
			loadAds.value = true
		} else if (indexedDB) {
			await getDynamicContentData(id, lang);
		} else {
			await getDynamicContentData(id, lang, true);
		}
	}

	const calcReadingTime = () => {
		if (showReadingTime.value === true) {
			const wordContainer = document.querySelector('main > article') as HTMLElement

			const allParagraphs = wordContainer?.querySelectorAll('p')
			const allH2 = wordContainer?.querySelectorAll('h2')
			const allH3 = wordContainer?.querySelectorAll('h3')
			const allH4 = wordContainer?.querySelectorAll('h4')
			const allLi = wordContainer?.querySelectorAll('li')

			const allNodes = [
				...Array.from(allParagraphs).map(element => element.textContent),
				...Array.from(allH2).map(element => element.textContent),
				...Array.from(allH3).map(element => element.textContent),
				...Array.from(allH4).map(element => element.textContent),
				...Array.from(allLi).map(element => element.textContent)
			]

			let wordString = allNodes.join(' ')
			let wordCount = wordString.split(' ').length

			readingTime.value = wordCount / 220
		}
	}

	const normalizePath = (path: string) => {
		const normalizedPath = `/${path}`.replace(/\/{2,}/g, '/')

		return normalizedPath === '/'
			? normalizedPath
			: `${normalizedPath.replace(/\/+$/, '')}/`
	}

	watch(
        () => route.path,
        async (newRoute) => {
			if (locale.value !== lang.value) {
				await requestStore.fillPageIndex(locale.value)
        		await requestStore.fillGlobals(locale.value)

				requestStore.setLanguage(locale.value)
			}

			let allPages = pageIndex.value?.global_index

			const normalizedRoutePath = normalizePath(route.path)

			const newPageID: any = Object.keys(allPages).find(key => normalizePath(allPages[key].locale_slug) === normalizedRoutePath)

			if (allPages[newPageID]?.menu === 'article') {
				showReadingTime.value = true
				showHistoryBack.value = true
				showRelatedNews.value = true
			}

			requestStore.setPageID(newPageID)

			await hydratePage(newPageID, locale.value)
        },
        { immediate: true, deep: true }
    )

	onMounted(() => {
		setTimeout(() => {
			calcReadingTime()
		}, 2000)
	})

	const transitionOrigin = ref<any>()

	transitionOrigin.value = `${localStorage.getItem("transition-origin-x")}px ${localStorage.getItem("transition-origin-y")}px`;
</script>

<style>
	.fade-up-enter-active,
	.fade-up-enter-from,
	.fade-up-leave-to {
		transform-origin: v-bind(transitionOrigin);
	}
</style>
